/**
 * services/EmailTemplateService.ts
 * Email template business logic and orchestration
 *
 * Handles email template management for the beta application approval workflow.
 * Supports approval, rejection, and waitlist email templates with variable substitution.
 *
 * Architecture: WU1.5 Task 7 - Repository Pattern
 * - All database access through emailTemplatesRepository
 * - Service layer handles business logic, validation, orchestration
 * - No direct Supabase access
 */

import { emailTemplatesRepository } from '@/repositories/emailTemplatesRepository';
import { emailCategoryService } from './EmailCategoryService';
import { Brand } from '@enfomentis/shared/config';
import type {
  EmailTemplate,
  EmailTemplateInsert,
  EmailTemplateUpdate,
  EmailTemplateType,
  UserManagementTemplateType
} from '@/types/supabase/EmailTemplate';

/**
 * Email Template Service
 *
 * Business logic layer for email template management.
 * Orchestrates template operations, validation, and seeding.
 * All database access delegated to emailTemplatesRepository.
 */
export class EmailTemplateService {
  constructor() {
    // TODO: [CONTRACTOR] Implement template performance analytics integration - track open rates, click-through rates, and engagement metrics
  }

  // ===== CORE CRUD METHODS =====

  /**
   * Add (create) new template
   *
   * @param templateData - Template data to create
   * @returns Promise resolving to created template
   */
  async add(templateData: EmailTemplateInsert): Promise<EmailTemplate> {
    try {
      return await emailTemplatesRepository.create(templateData);
    } catch (error) {
      console.error('Failed to create template:', error);
      throw new Error('Failed to create email template');
    }
  }

  /**
   * Update existing template by ID
   *
   * NOTE: For optimistic concurrency, this method fetches current template first
   * to get prevUpdatedAt. For explicit concurrency control, use repository directly.
   *
   * @param id - Template ID
   * @param updates - Template updates
   * @returns Promise resolving to updated template
   */
  async update(id: string, updates: EmailTemplateUpdate): Promise<EmailTemplate> {
    try {
      // Fetch current template to get updatedAt for concurrency
      const current = await emailTemplatesRepository.getById(id);
      if (!current) {
        throw new Error(`Template ${id} not found`);
      }

      return await emailTemplatesRepository.update(id, updates, current.updatedAt);
    } catch (error) {
      console.error(`Failed to update template ${id}:`, error);
      throw new Error('Failed to update email template');
    }
  }

  // ===== SPECIALIZED QUERY METHODS =====

  /**
   * Get template by type
   *
   * @param templateType - Type of template to retrieve
   * @returns Promise resolving to template or null if not found
   */
  async getByType(templateType: EmailTemplateType): Promise<EmailTemplate | null> {
    try {
      return await emailTemplatesRepository.getByType(templateType);
    } catch (error) {
      console.error(`Failed to get template by type ${templateType}:`, error);
      throw new Error(`Failed to retrieve ${templateType} template`);
    }
  }

  /**
   * Get all templates grouped by type
   *
   * @returns Promise resolving to templates by type
   */
  async getAllTemplates(): Promise<Record<string, EmailTemplate | null>> {
    try {
      return await emailTemplatesRepository.getAllTemplates();
    } catch (error) {
      console.error('Failed to get all templates:', error);
      throw new Error('Failed to retrieve email templates');
    }
  }

  /**
   * Update template by type
   *
   * @param templateType - Type of template to update
   * @param updateData - Template data to update
   * @param adminUsername - Admin making the update
   * @param prevUpdatedAt - Previous updatedAt for optimistic concurrency
   * @returns Promise resolving to updated template
   */
  async updateByType(
    templateType: EmailTemplateType,
    updateData: Omit<EmailTemplateUpdate, 'updatedBy'>,
    adminUsername: string,
    prevUpdatedAt: string
  ): Promise<EmailTemplate> {
    try {
      // TODO: [CONTRACTOR] Implement template audit trail and versioning - track all template changes for compliance and rollback capabilities

      return await emailTemplatesRepository.updateByType(
        templateType,
        {
          ...updateData,
          updatedBy: adminUsername
        },
        prevUpdatedAt
      );
    } catch (error) {
      console.error(`Failed to update template ${templateType}:`, error);
      throw new Error(`Failed to update ${templateType} template`);
    }
  }

  // ===== TEMPLATE SEEDING METHODS =====

  /**
   * Seed default templates if they don't exist
   *
   * @param adminUsername - Admin username for template creation
   * @returns Promise resolving when seeding is complete
   */
  async seedDefaultTemplates(adminUsername: string = 'system'): Promise<void> {
    try {
      console.log('🌱 Seeding default email templates...');

      // Ensure Beta Applications category exists
      const betaCategory = await emailCategoryService.ensureCategoryExists(
        'Beta Applications',
        'Email templates for beta application workflow'
      );

      const betaCategoryId = betaCategory.id;

      const defaultTemplates: EmailTemplateInsert[] = [
        {
          categoryId: betaCategoryId,
          templateType: 'approval',
          signature: null,
          subject: `Welcome to ${Brand.displayName} Beta Program! 🎉`,
          content: `Hi {{applicant_name}},

Congratulations! Your application for the ${Brand.displayName} Beta Program has been approved!

We're excited to have you join our community of traders working to improve their trading performance through better journaling and analysis.

**Next Steps:**
1. You'll receive access instructions within the next 24-48 hours
2. Join our Discord community for beta testers
3. Start exploring ${Brand.displayName}'s features and provide feedback

**Application Details:**
- Submitted: {{application_date}}
- Approved: {{decision_date}}
- Approved by: {{admin_name}}

Thank you for your interest in ${Brand.displayName}. We look forward to your feedback and contributions to making ${Brand.displayName} the best trading journal platform available.

Best regards,
The ${Brand.displayName} Team
${Brand.supportEmail}`,
          updatedBy: adminUsername
        },
        {
          categoryId: betaCategoryId,
          templateType: 'rejection',
          signature: null,
          subject: `${Brand.displayName} Beta Application Update`,
          content: `Hi {{applicant_name}},

Thank you for your interest in the ${Brand.displayName} Beta Program.

After careful review, we've decided not to move forward with your application at this time. This decision was based on our current beta testing capacity and the specific mix of testers we're looking for in this phase.

**This doesn't mean you won't have future opportunities:**
- We may open additional beta rounds
- You'll be notified of future testing opportunities
- The full release will be available to everyone

**Application Details:**
- Submitted: {{application_date}}
- Reviewed: {{decision_date}}

We truly appreciate your interest in ${Brand.displayName} and encourage you to stay connected for future opportunities.

Best regards,
The ${Brand.displayName} Team
${Brand.supportEmail}`,
          updatedBy: adminUsername
        },
        {
          categoryId: betaCategoryId,
          templateType: 'waitlist',
          signature: null,
          subject: `${Brand.displayName} Beta Application - You're on Our Waitlist! ⏰`,
          content: `Hi {{applicant_name}},

Thank you for your application to the ${Brand.displayName} Beta Program.

Your application has been reviewed and you've been placed on our **priority waitlist**. This means you're a strong candidate, but we've reached our current beta testing capacity.

**What this means:**
- You're in line for the next available beta spot
- We expect to have additional openings within 2-4 weeks
- You'll be notified immediately when a spot becomes available
- No need to reapply - you're already in the queue

**Application Details:**
- Submitted: {{application_date}}
- Waitlisted: {{decision_date}}
- Reviewed by: {{admin_name}}

We'll keep you updated on your status and notify you as soon as a spot opens up.

Thank you for your patience and interest in ${Brand.displayName}!

Best regards,
The ${Brand.displayName} Team
${Brand.supportEmail}`,
          updatedBy: adminUsername
        },
        {
          categoryId: betaCategoryId,
          templateType: 'confirmation',
          signature: null,
          subject: `${Brand.displayName} Beta Application Received! 📋`,
          content: `Hi {{applicant_name}},

Thank you for submitting your application to the ${Brand.displayName} Beta Program!

We've successfully received your application and our team will review it carefully. Here's what happens next:

**Review Process:**
• Our team typically reviews applications within 3-5 business days
• You'll receive an email update once we've made a decision
• We're looking for engaged traders who can provide valuable feedback

**Application Details:**
- Submitted: {{application_date}}
- Application ID: {{application_id}}
- Email: This confirmation was sent to the email address you provided

**What to Expect:**
If selected, you'll receive access instructions and an invitation to our beta community where you can connect with other traders and provide feedback on ${Brand.displayName} features.

Thank you for your interest in ${Brand.displayName}. We appreciate your patience during the review process!

Best regards,
The ${Brand.displayName} Team
${Brand.supportEmail}`,
          updatedBy: adminUsername
        }
      ];

      // Check which templates already exist
      const existingTemplates = await this.getAllTemplates();

      // Insert missing templates
      for (const template of defaultTemplates) {
        if (!existingTemplates[template.templateType]) {
          await emailTemplatesRepository.create(template);
          console.log(`✅ Created ${template.templateType} template`);
        } else {
          console.log(`⏭️  ${template.templateType} template already exists`);
        }
      }

      console.log('🌱 Default template seeding complete');
    } catch (error) {
      console.error('Failed to seed default templates:', error);
      throw new Error('Failed to initialize email templates');
    }
  }

  /**
   * Reset templates to defaults
   *
   * @param adminUsername - Admin username for template reset
   * @returns Promise resolving when reset is complete
   */
  async resetToDefaults(adminUsername: string): Promise<void> {
    try {
      console.log('🔄 Resetting email templates to defaults...');

      // Delete all existing templates
      await emailTemplatesRepository.deleteAll();

      // Seed default templates
      await this.seedDefaultTemplates(adminUsername);

      console.log('🔄 Template reset complete');
    } catch (error) {
      console.error('Failed to reset templates:', error);
      throw new Error('Failed to reset email templates to defaults');
    }
  }

  // ===== USER MANAGEMENT TEMPLATE METHODS =====

  /**
   * Get template by User Management template type
   *
   * @param templateType - User Management template type to retrieve
   * @returns Promise resolving to template or null if not found
   */
  async getUserManagementTemplate(templateType: UserManagementTemplateType): Promise<EmailTemplate | null> {
    try {
      return await emailTemplatesRepository.getByType(templateType);
    } catch (error) {
      console.error(`Failed to get User Management template ${templateType}:`, error);
      throw new Error(`Failed to retrieve ${templateType} template`);
    }
  }

  /**
   * Seed default User Management templates if they don't exist
   *
   * @param adminUsername - Admin username for template creation
   * @returns Promise resolving when seeding is complete
   */
  async seedUserManagementTemplates(adminUsername: string = 'system'): Promise<void> {
    try {
      console.log('🌱 Seeding User Management email templates...');

      // Ensure User Management category exists
      const userMgmtCategory = await emailCategoryService.ensureCategoryExists(
        'User Management',
        'User invitation, activation, and account management emails'
      );

      const userMgmtCategoryId = userMgmtCategory.id;

      const userMgmtTemplates = [
        {
          categoryId: userMgmtCategoryId,
          templateType: 'invitation' as const,
          signature: null,
          subject: `You're Invited to Join ${Brand.displayName}! 🎉`,
          content: `Hi {{recipient_name}},

Great news! Your beta application has been approved and you're now invited to activate your ${Brand.displayName} account.

**Account Activation:**
To get started, please click the secure link below to set up your password and activate your account:

{{invitation_link}}

Alternatively, you can visit https://${Brand.domain}/activate and enter your activation code: {{invitation_token}}

**Important Details:**
• This invitation expires on {{expires_at}}
• You'll have full access to all ${Brand.displayName} beta features
• Join our Discord community for support and feedback
• Your account was approved by {{sent_by}} on {{sent_date}}

**What to Expect:**
Once you activate your account, you'll be able to:
• Create and manage your trading journal
• Track your performance and analytics
• Connect with other beta testers
• Provide feedback on new features

If you have any questions or need assistance, please don't hesitate to reach out to our support team.

Welcome to the ${Brand.displayName} community!

Best regards,
The ${Brand.displayName} Team
${Brand.supportEmail}

---
This invitation was sent on {{sent_date}}. If you didn't request this invitation, please ignore this email.`,
          updatedBy: adminUsername
        },
        {
          categoryId: userMgmtCategoryId,
          templateType: 'activation-reminder' as const,
          signature: null,
          subject: `Reminder: Complete Your ${Brand.displayName} Account Activation ⏰`,
          content: `Hi {{recipient_name}},

This is a friendly reminder that your invitation to join ${Brand.displayName} is still pending activation.

**Your Invitation Details:**
• Invited on: {{sent_date}}
• Expires: {{expires_at}}
• Sent by: {{sent_by}}

**Activate Your Account:**
Click the link below to set up your password and start using ${Brand.displayName}:

{{invitation_link}}

Or visit https://${Brand.domain}/activate and enter your code: {{invitation_token}}

**Don't Miss Out:**
• Your invitation will expire soon
• You'll lose access to the beta program if not activated
• It only takes 2 minutes to complete setup

**Need Help?**
If you're having trouble with activation or have any questions, please contact our support team at ${Brand.supportEmail}.

We're excited to have you join our trading community!

Best regards,
The ${Brand.displayName} Team
${Brand.supportEmail}

---
This is an automated reminder. If you've already activated your account, please ignore this email.`,
          updatedBy: adminUsername
        },
        {
          categoryId: userMgmtCategoryId,
          templateType: 'password-reset' as const,
          signature: null,
          subject: `Reset Your ${Brand.displayName} Password 🔐`,
          content: `Hi {{user_name}},

We received a request to reset your password for your ${Brand.displayName} account.

**Reset Your Password:**
Click the secure link below to create a new password:

{{reset_link}}

Or visit https://${Brand.domain}/password-reset and enter your reset code: {{reset_token}}

**Important Details:**
• This reset link expires on {{expires_at}}
• The link can only be used once
• If you didn't request this reset, please ignore this email
• Your account remains secure - no changes will be made without using this link

**Need Help?**
If you're having trouble resetting your password or have any security concerns, please contact our support team at ${Brand.supportEmail}.

**For Your Security:**
• Never share this reset link with anyone
• Only use official ${Brand.displayName} links for password resets
• If you suspect unauthorized access, contact support immediately

Best regards,
The ${Brand.displayName} Team
${Brand.supportEmail}

---
Password reset requested on {{sent_date}}. This is an automated security email.`,
          updatedBy: adminUsername
        }
      ];

      // Check which templates already exist
      for (const template of userMgmtTemplates) {
        const existing = await this.getUserManagementTemplate(template.templateType as UserManagementTemplateType);

        if (!existing) {
          await emailTemplatesRepository.create(template as unknown as EmailTemplateInsert);
          console.log(`✅ Created ${template.templateType} template`);
        } else {
          console.log(`⏭️  ${template.templateType} template already exists`);
        }
      }

      console.log('🌱 User Management template seeding complete');
    } catch (error) {
      console.error('Failed to seed User Management templates:', error);
      throw new Error('Failed to initialize User Management templates');
    }
  }

  /**
   * Validate User Management template content for required variables
   *
   * @param content - Template content to validate
   * @param templateType - User Management template type for context
   * @returns Validation result with any warnings
   */
  validateUserManagementTemplate(content: string, templateType: UserManagementTemplateType): {
    isValid: boolean;
    warnings: string[];
    requiredVariables: string[];
    foundVariables: string[];
  } {
    const baseRequiredVariables = ['{{user_name}}', '{{sent_date}}'];

    let requiredVariables: string[];
    switch (templateType) {
      case 'password-reset':
        requiredVariables = [...baseRequiredVariables, '{{reset_link}}', '{{reset_token}}', '{{expires_at}}'];
        break;
      default:
        requiredVariables = baseRequiredVariables;
    }

    const foundVariables = [...content.matchAll(/\{\{([^}]+)\}\}/g)].map(match => match[0]);
    const missingRequired = requiredVariables.filter(variable => !content.includes(variable));

    const warnings: string[] = [];

    if (missingRequired.length > 0) {
      warnings.push(`Missing required variables: ${missingRequired.join(', ')}`);
    }

    if (content.length < 100) {
      warnings.push('Template content seems very short');
    }

    if (content.length > 5000) {
      warnings.push('Template content is very long (over 5000 characters)');
    }

    // Check for password reset link security
    if (templateType === 'password-reset') {
      if (!content.includes('{{reset_link}}')) {
        warnings.push('Missing secure password reset link');
      }
      if (!content.includes('{{expires_at}}')) {
        warnings.push('Missing expiration information for security');
      }
      if (!content.includes('{{reset_token}}')) {
        warnings.push('Missing reset token for backup access');
      }
    }

    return {
      isValid: missingRequired.length === 0,
      warnings,
      requiredVariables,
      foundVariables
    };
  }

  // ===== VALIDATION METHODS =====

  /**
   * Validate template content for required variables
   *
   * @param content - Template content to validate
   * @param templateType - Type of template for context
   * @returns Validation result with any warnings
   */
  validateTemplateContent(content: string, _templateType: EmailTemplateType): {
    isValid: boolean;
    warnings: string[];
    requiredVariables: string[];
    foundVariables: string[];
  } {
    const requiredVariables = ['{{applicant_name}}', '{{application_date}}', '{{decision_date}}'];
    // const _optionalVariables = ['{{admin_name}}', '{{admin_notes}}'];
    // TODO: [CONTRACTOR] Implement enhanced template validation - security scanning, variable validation, and compliance checks

    const foundVariables = [...content.matchAll(/\{\{([^}]+)\}\}/g)].map(match => match[0]);
    const missingRequired = requiredVariables.filter(variable => !content.includes(variable));

    const warnings: string[] = [];

    if (missingRequired.length > 0) {
      warnings.push(`Missing required variables: ${missingRequired.join(', ')}`);
    }

    if (content.length < 100) {
      warnings.push('Template content seems very short');
    }

    if (content.length > 5000) {
      warnings.push('Template content is very long (over 5000 characters)');
    }

    return {
      isValid: missingRequired.length === 0,
      warnings,
      requiredVariables,
      foundVariables
    };
  }
}

// Export singleton instance
export const emailTemplateService = new EmailTemplateService();